import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import { ROUTES } from '@aibees/elephant/router';
import { SearchType } from '@aibees/elephant/types';
import { Icon, LookalikeButtonBase } from '@aibees/shared/components';
import { useMatchRoute } from '@aibees/shared/hooks';
import { encodeQueryParamValues } from '@aibees/shared/utils';
import {
  ClickAwayListener,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { memo, useState } from 'react';
import { RelevancySummary } from '../../../relevancy-summary';
import { LookalikeButtonTooltipContent } from '../lookalike-button-tooltip-content';
import type { LookalikeButtonSidebarProps as Props } from './types';
import { useLookalikeSeeds } from '@aibees/elephant/components/company-similarity-search/hooks/use-lookalike-seeds';

export const LookalikeButtonSidebar = memo(
  ({
    currentItem,
    lookalikeKey,
    maxStickyRows,
    onAddToCurrentSearch,
    selected,
    hideScore,
    disabled,
  }: Props) => {
    const type = useSearchTypeFromRoute();
    const isCompanyPage = useMatchRoute(ROUTES.company);
    const isPeoplePage = useMatchRoute(ROUTES.people);
    const isEmployeeTab = useMatchRoute(ROUTES.companyEmployees);
    const isPeopleLookalike = useMatchRoute(ROUTES.peopleLookalike);
    const isCompanyLookalike = useMatchRoute(ROUTES.companyLookalike);
    const isLookalikeMainPage = isPeopleLookalike || isCompanyLookalike;
    const items = useLookalikeSeeds();

    const [open, setOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

    const changeOpen = (value: boolean) => {
      setOpen(value);
      if (value) {
        setTimeout(() => {
          setOpen(false);
        }, 5000);
      }
    };

    const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setMenuAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
      setMenuAnchorEl(null);
    };

    const handleAddOrRemoveFromCurrentSearch = () => {
      onAddToCurrentSearch();
      handleMenuClose();
    };

    const handleStartNewSearch = () => {
      const itemName =
        'summary' in currentItem
          ? currentItem.summary?.name
          : currentItem.profile?.full_name;
      if (!lookalikeKey || !itemName) return;

      const newParams = {
        value: encodeQueryParamValues([lookalikeKey]),
        name: itemName,
      };

      let baseUrl: string;

      if (
        isLookalikeMainPage ||
        isCompanyPage ||
        isPeoplePage ||
        isEmployeeTab
      ) {
        baseUrl =
          isCompanyPage || type === SearchType.Company
            ? `/search/company?`
            : `/search/people?`;
      } else {
        baseUrl = isEmployeeTab
          ? `${window.location.origin}${ROUTES.searchPeople}`
          : window.location.href;
      }

      const newUrl = new URL(baseUrl, window.location.origin);
      newUrl.search = new URLSearchParams(
        newParams as Record<string, string>
      ).toString();

      window.open(newUrl.toString(), '_blank');
      handleMenuClose();
    };

    return (
      <>
        {!isEmployeeTab && !hideScore && !!currentItem.relevancy && (
          <RelevancySummary relevancy={currentItem.relevancy} />
        )}
        {disabled ? (
          <Tooltip title="Coming soon!" arrow>
            <span>
              <LookalikeButtonBase
                type="icon"
                onClick={handleMenuClick}
                disabled={disabled}
              />
            </span>
          </Tooltip>
        ) : (
          <ClickAwayListener onClickAway={() => changeOpen(false)}>
            <Tooltip
              onClose={() => changeOpen(false)}
              title={
                <LookalikeButtonTooltipContent
                  max={maxStickyRows ?? 0}
                  onClose={() => changeOpen(false)}
                />
              }
              arrow
              placement="top"
            >
              <LookalikeButtonBase
                limitReached={items.length > 5}
                errorMessage="You have reached the limit of 5 lookalikes, Remove to add new ones."
                type="icon"
                onClick={handleMenuClick}
              />
            </Tooltip>
          </ClickAwayListener>
        )}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          {!(
            isLookalikeMainPage ||
            isCompanyPage ||
            isPeoplePage ||
            isEmployeeTab
          ) && (
            <MenuItem
              onClick={handleAddOrRemoveFromCurrentSearch}
              disabled={isEmployeeTab}
            >
              <Icon name="fingerprint" size={16} color="grey.500" />
              <Typography color="grey.700" variant="body4" ml={0.5}>
                {selected
                  ? 'Remove from Current Search'
                  : 'Add to Current Search'}
              </Typography>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => handleStartNewSearch()}
            disabled={!currentItem.canSimilaritySearch}
          >
            <Icon name="fingerprint" size={16} color="grey.500" />{' '}
            <Typography color="grey.700" variant="body4" ml={0.5}>
              Start New Search
            </Typography>
          </MenuItem>
        </Menu>
      </>
    );
  }
);

LookalikeButtonSidebar.displayName = 'LookalikeButtonSidebar';
